import { api, unwrapArray } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { parseCsv } from "@/lib/csv";
import type { ReceiptLineDraft, RollDraft } from "@/types/inventory";

/** What the opening-stock CSV looks like; shown to the user and offered as a download */
export const STOCK_CSV_HEADER = "product_code,variant_code,lot_number,roll_no,quantity,net_weight,gross_weight,grade,location";
export const STOCK_CSV_SAMPLE = `${STOCK_CSV_HEADER}\nPOPLIN-40S,NAVY,L-2026-01,R001,100,22.5,23,FRESH,A-1\nPOPLIN-40S,NAVY,L-2026-01,R002,98.5,22,22.6,FRESH,A-1`;

const norm = (s: string) => s.trim().toLowerCase().replace(/[\s-]+/g, "_");

export interface ImportResult {
  lines: ReceiptLineDraft[];
  problems: string[];
}

let keySeq = 0;
export const newLineKey = () => `line-${Date.now()}-${keySeq++}`;

/**
 * Reads a stock CSV (one row per roll) into receipt lines: rolls of the same product, shade and lot go on one
 * line. Product and shade codes are looked up against the catalog; rows that can't be matched are listed.
 */
export async function importStockCsv(text: string): Promise<ImportResult> {
  const rows = parseCsv(text).filter((r) => r.some((c) => c.trim()));
  const problems: string[] = [];
  if (rows.length < 2) return { lines: [], problems: ["The file has no rows. The first row must be the column names."] };
  const head = (rows[0] || []).map(norm);
  const col = (...names: string[]) => head.findIndex((h) => names.includes(h));
  const iProduct = col("product_code", "product", "code");
  const iVariant = col("variant_code", "shade", "shade_code", "variant");
  const iLot = col("lot_number", "lot", "batch");
  const iRoll = col("roll_no", "roll", "roll_number", "unit_no");
  const iQty = col("quantity", "qty", "length", "meters", "metres");
  const iNet = col("net_weight", "net_wt");
  const iGross = col("gross_weight", "gross_wt");
  const iGrade = col("grade");
  const iLoc = col("location", "rack");
  if (iProduct < 0 || iRoll < 0 || iQty < 0) {
    return { lines: [], problems: ["The first row must have at least the columns product_code, roll_no and quantity."] };
  }

  const products = new Map<string, { id: string; name: string; unit: string } | null>();
  const variants = new Map<string, Map<string, string>>();
  const lookupProduct = async (code: string) => {
    const key = code.toLowerCase();
    if (products.has(key)) return products.get(key) || null;
    let found: { id: string; name: string; unit: string } | null = null;
    try {
      const page = unwrapPage<any>(await api.get(`/api/Product/List?search=${encodeURIComponent(code)}&page_size=20`));
      const hit = page.rows.find((p) => String(p.code).toLowerCase() === key);
      if (hit) found = { id: hit.id, name: `${hit.name} (${hit.code})`, unit: hit.unit };
    } catch {
      found = null;
    }
    products.set(key, found);
    return found;
  };
  const lookupVariant = async (productId: string, code: string) => {
    if (!variants.has(productId)) {
      const map = new Map<string, string>();
      try {
        for (const v of unwrapArray<any>(await api.get(`/api/Product/Variants/${productId}`))) {
          map.set(String(v.code).toLowerCase(), v.id);
          map.set(String(v.name).toLowerCase(), v.id);
        }
      } catch {
        /* the code is then reported as unknown */
      }
      variants.set(productId, map);
    }
    return variants.get(productId)!.get(code.toLowerCase()) || null;
  };

  const grouped = new Map<string, ReceiptLineDraft>();
  for (let n = 1; n < rows.length; n++) {
    const r = rows[n]!;
    const cell = (i: number) => (i >= 0 ? (r[i] || "").trim() : "");
    const line = n + 1;
    const code = cell(iProduct);
    if (!code) {
      problems.push(`Row ${line}: no product code`);
      continue;
    }
    const p = await lookupProduct(code);
    if (!p) {
      problems.push(`Row ${line}: product "${code}" is not in the catalog`);
      continue;
    }
    let variantId = "";
    if (cell(iVariant)) {
      const v = await lookupVariant(p.id, cell(iVariant));
      if (!v) {
        problems.push(`Row ${line}: ${p.name} has no shade "${cell(iVariant)}"`);
        continue;
      }
      variantId = v;
    }
    const qty = Number(cell(iQty));
    if (!cell(iRoll) || !(qty > 0)) {
      problems.push(`Row ${line}: needs a roll number and a quantity above zero`);
      continue;
    }
    const lot = cell(iLot);
    const key = `${p.id}|${variantId}|${lot.toLowerCase()}`;
    let ln = grouped.get(key);
    if (!ln) {
      ln = { key: newLineKey(), product_id: p.id, product_label: p.name, variant_id: variantId, lot_number: lot, unit: p.unit, variants: [], rolls: [] };
      grouped.set(key, ln);
    }
    const grade = cell(iGrade).toUpperCase();
    const roll: RollDraft = {
      unit_no: cell(iRoll),
      quantity: String(qty),
      net_weight: cell(iNet),
      gross_weight: cell(iGross),
      grade: ["FRESH", "SECOND", "REJECT"].includes(grade) ? grade : "FRESH",
      location: cell(iLoc),
    };
    ln.rolls.push(roll);
  }

  // Shade choices for each product, so a line can still be edited after the import
  const lines = [...grouped.values()];
  for (const ln of lines) {
    const raw = unwrapArray<any>(await api.get(`/api/Product/Variants/${ln.product_id}`).catch(() => []));
    ln.variants = raw.map((v) => ({ id: v.id, name: v.name, code: v.code }));
  }
  return { lines, problems };
}
